/**
 * ReporterSectionWork — per-section workspace.
 *
 * Two backends, one workspace:
 *
 * LEGACY sections (I, II.5, III, IV, V.1, V.2, VI, VII, VIII, X and preflight
 * for IX) call the original /api/reporter/validate/section/:id and
 * /api/reporter/preflight endpoints via SECTION_DISPATCH.
 *   - 'preflight' — File check. Drop in just the .txt.
 *   - 'full'      — File check + Records recount.
 *
 * CANONICAL measures (II, II.CDR, MTM, MPPP, I.D, V.1.D, V.2.D, II.5.D) call
 * the canonical engine stack via CANONICAL_DISPATCH:
 *   - 'preflight' — POST /api/reporter/file-shape/:schemaId   (submission only)
 *   - 'full'      — POST /api/reporter/validate-canonical/:measureId
 *                   (submission + census; file shape + recount + reconcile)
 *   - 'recount'   — POST /api/reporter/recount/:schemaId      (census only)
 * Each canonical tile also offers "Build submission file", which POSTs the
 * census to /api/reporter/build/:measureId and downloads the returned .TXT.
 *
 * Both dispatch tables are exported for the smoke test.
 */

import React, { useState, useRef } from 'react';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';

const API = process.env.REACT_APP_API_URL;

// Per-section backend dispatch.
//   endpoint(sectionId, part)  -> POST URL relative to /api/reporter
//   files                      -> ordered list of FileDrop specs to render in 'full' mode
//   extraBody(state)           -> extra body fields (optional)
//   needsCensus                -> if false, 'full' mode collapses into 'preflight' (no records layout published)
export const SECTION_DISPATCH = {
  'I':     { endpoint: () => '/api/reporter/validate/section/I',
             files: [
               { key: 'universe',  label: 'Records — Grievances universe (ODAG GRV_C)', desc: 'The case-level grievance records your file was built from.' },
               { key: 'submission', label: 'HPMS file (.txt)', desc: "The tab-delimited file you're about to upload to HPMS." },
               { key: 'supplementaryDismissed', label: 'Dismissed grievances (optional)', desc: 'Populates Element E. ODAG GRV_C does not include dismissed cases, so this comes separately.', optional: true },
             ] },
  'II.1':  { endpoint: () => '/api/reporter/validate/section/II.1',
             files: [
               { key: 'universe',  label: 'Records — Organization Determinations (ODAG OD, Table 1)', desc: 'Case-level OD records.' },
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The .txt you are about to upload.' },
               { key: 'pymtC', label: 'Payment ODs (optional, PYMT_C)', desc: 'If your plan reports payment ODs separately.', optional: true },
               { key: 'withdrawnCases', label: 'Withdrawn cases (optional)', desc: 'CSV of withdrawn case IDs for Element II.x.B.', optional: true },
               { key: 'dispositionOverrides', label: 'Disposition overrides (optional)', desc: 'CSV: case_id, derived_disposition.', optional: true },
             ] },
  'II.2':  { endpoint: () => '/api/reporter/validate/section/II.1',
             files: [
               { key: 'universe',  label: 'Records — Organization Determinations (ODAG OD, Table 1)', desc: 'Case-level OD records.' },
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The .txt you are about to upload.' },
               { key: 'pymtC', label: 'Payment ODs (optional, PYMT_C)', desc: 'If your plan reports payment ODs separately.', optional: true },
               { key: 'withdrawnCases', label: 'Withdrawn cases (optional)', desc: 'CSV of withdrawn case IDs.', optional: true },
               { key: 'dispositionOverrides', label: 'Disposition overrides (optional)', desc: 'CSV: case_id, derived_disposition.', optional: true },
             ] },
  'II.3':  { endpoint: () => '/api/reporter/validate/section/II.3',
             files: [
               { key: 'universe',  label: 'Records — Reconsiderations (ODAG RECON, Table 2)', desc: 'Case-level reconsideration records.' },
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The .txt you are about to upload.' },
               { key: 'pymtC', label: 'Payment Recons (optional, PYMT_C)', desc: 'If your plan reports payment recons separately.', optional: true },
               { key: 'withdrawnCases', label: 'Withdrawn cases (optional)', desc: 'CSV of withdrawn case IDs.', optional: true },
               { key: 'dispositionOverrides', label: 'Disposition overrides (optional)', desc: 'CSV: case_id, derived_disposition.', optional: true },
             ] },
  'II.4':  { endpoint: () => '/api/reporter/validate/section/II.3',
             files: [
               { key: 'universe',  label: 'Records — Reconsiderations (ODAG RECON, Table 2)', desc: 'Case-level reconsideration records.' },
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The .txt you are about to upload.' },
               { key: 'pymtC', label: 'Payment Recons (optional, PYMT_C)', desc: 'If your plan reports payment recons separately.', optional: true },
               { key: 'withdrawnCases', label: 'Withdrawn cases (optional)', desc: 'CSV of withdrawn case IDs.', optional: true },
               { key: 'dispositionOverrides', label: 'Disposition overrides (optional)', desc: 'CSV: case_id, derived_disposition.', optional: true },
             ] },
  'II.5':  { endpoint: () => '/api/reporter/validate/section/II.5',
             primaryFileKey: 'reopeningsFile',
             files: [
               { key: 'reopeningsFile', label: 'Re-openings file (case-level)', desc: "The case-level re-openings file you're about to submit. One row per re-opened case." },
               { key: 'odUniverse',  label: 'Records — OD universe (optional)', desc: 'Cross-checks each re-opened case ID exists in your OD records.', optional: true },
               { key: 'reconUniverse',  label: 'Records — RECON universe (optional)', desc: 'Cross-checks each re-opened case ID exists in your RECON records.', optional: true },
             ] },
  'III':   { endpoint: () => '/api/reporter/validate/section/III',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Employer Group Plan Sponsors .txt.' },
               { key: 'census', label: 'Records — Employer roster', desc: 'Your roster of employer group plan sponsors (one row per employer/PBP).' },
             ] },
  'IV':    { endpoint: () => '/api/reporter/validate/section/IV',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The SNP HRA .txt.' },
               { key: 'census', label: 'Records — HRA activity roster', desc: 'One row per enrollee, with HRA performed/refused/unreachable status.' },
             ] },
  'V.1':   { endpoint: () => '/api/reporter/validate/section/V.1',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Enrollment .txt.' },
               { key: 'census', label: 'Records — Enrollment activity', desc: 'One row per enrollment request, with received date, channel, outcome.' },
             ] },
  'V.2':   { endpoint: () => '/api/reporter/validate/section/V.2',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Disenrollment .txt.' },
               { key: 'census', label: 'Records — Disenrollment activity', desc: 'One row per disenrollment request, voluntary and involuntary.' },
             ] },
  'VI':    { endpoint: () => '/api/reporter/validate/section/VI',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Rewards & Incentives .txt (one record per program).' },
               { key: 'census', label: 'Records — Rewards program roster', desc: 'One row per program, with enrollee and reward counts.' },
             ] },
  'VII':   { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The consolidated Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider payment records', desc: 'Provider payment universe for the reporting period.' },
             ] },
  'VII.1': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider payment records', desc: 'Provider payment universe for the reporting period.' },
             ] },
  'VII.2': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider payment records', desc: 'Provider payment universe for the reporting period.' },
             ] },
  'VII.3': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider payment records', desc: 'Provider payment universe for the reporting period.' },
             ] },
  'VII.4': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider payment records', desc: 'Provider payment universe for the reporting period.' },
             ] },
  'VII.5': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt.' },
               { key: 'census', label: 'Records — Provider roster', desc: 'Provider directory used to build the .txt.' },
             ] },
  'VII.6': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt (PCP/PCG ACO Metrics).' },
               { key: 'census', label: 'Records — ACO metrics roster', desc: 'PCP/PCG ACO records used to build the .txt.' },
             ] },
  'VII.7': { endpoint: () => '/api/reporter/validate/section/VII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Payments to Providers .txt (Non-PCP ACO Metric).' },
               { key: 'census', label: 'Records — Non-PCP ACO metric records', desc: 'Non-PCP ACO records used to build the .txt.' },
             ] },
  'VIII':  { endpoint: () => '/api/reporter/validate/section/VIII',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The Supplemental Benefit Utilization .txt.' },
               { key: 'census', label: 'Records — Supplemental benefit utilization', desc: 'Utilization records per category, offering, network.' },
             ] },
  'IX':    { endpoint: () => '/api/reporter/preflight', needsCensus: false,
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The D-SNP Enrollee Advisory Committee .txt. (File check only — no published records layout.)' },
             ] },
  'X':     { endpoint: () => '/api/reporter/validate/section/X',
             files: [
               { key: 'submission', label: 'HPMS file (.txt)', desc: 'The D-SNP Admission Notifications .txt.' },
               { key: 'census', label: 'Records — Admission notification log', desc: 'Hospital/SNF admissions for high-risk duals, with state-notified flag.' },
             ] },
};

// ---------------------------------------------------------------------------
// CANONICAL_DISPATCH — tiles served by the canonical engine registry
// (server/services/reporter/engineRegistry.js).
//   measureId — registry key, used by /validate-canonical/:measureId and
//               /build/:measureId
//   schemaId  — file-shape schema id, used by /file-shape/:schemaId and
//               /recount/:schemaId (the recount routes use the hyphenated ids)
// ---------------------------------------------------------------------------
export const CANONICAL_DISPATCH = {
  'II': {
    measureId: 'odr', schemaId: 'odr',
    validateEndpoint: '/api/reporter/validate-canonical/odr',
    recountEndpoint:  '/api/reporter/recount/odr',
    fileShapeEndpoint:'/api/reporter/file-shape/odr',
    buildEndpoint:    '/api/reporter/build/odr',
    censusLabel: 'Records — OD & Reconsideration case universe',
    censusDesc:  'Case-level organization determination and reconsideration records the file was built from.',
    submissionDesc: 'The consolidated Organization Determinations & Reconsiderations .txt (single wide row per contract).',
  },
  'II.CDR': {
    measureId: 'cdr', schemaId: 'cdr',
    validateEndpoint: '/api/reporter/validate-canonical/cdr',
    recountEndpoint:  '/api/reporter/recount/cdr',
    fileShapeEndpoint:'/api/reporter/file-shape/cdr',
    buildEndpoint:    '/api/reporter/build/cdr',
    censusLabel: 'Records — CD & Redetermination case universe',
    censusDesc:  'Case-level coverage determination and redetermination records the file was built from.',
    submissionDesc: 'The Coverage Determinations & Redeterminations .txt.',
  },
  'MTM': {
    measureId: 'mtm', schemaId: 'mtm',
    validateEndpoint: '/api/reporter/validate-canonical/mtm',
    recountEndpoint:  '/api/reporter/recount/mtm',
    fileShapeEndpoint:'/api/reporter/file-shape/mtm',
    buildEndpoint:    '/api/reporter/build/mtm',
    censusLabel: 'Records — MTM beneficiary roster',
    censusDesc:  'One row per MTM-eligible beneficiary. PHI stays in your session; per-row traces are never written to the audit log.',
    submissionDesc: 'The MTM .txt (one record per beneficiary).',
  },
  'MPPP': {
    measureId: 'm3p', schemaId: 'm3p',
    validateEndpoint: '/api/reporter/validate-canonical/m3p',
    recountEndpoint:  '/api/reporter/recount/m3p',
    fileShapeEndpoint:'/api/reporter/file-shape/m3p',
    buildEndpoint:    '/api/reporter/build/m3p',
    censusLabel: 'Records — M3P participation roster',
    censusDesc:  'One row per enrollee event, per plan. Drives the per-plan participation counts.',
    submissionDesc: 'The Medicare Prescription Payment Plan .txt (one row per plan).',
  },
  'I': {
    measureId: 'grievances_c', schemaId: 'grievances-c',
    validateEndpoint: '/api/reporter/validate-canonical/grievances_c',
    recountEndpoint:  '/api/reporter/recount/grievances-c',
    fileShapeEndpoint:'/api/reporter/file-shape/grievances-c',
    buildEndpoint:    '/api/reporter/build/grievances_c',
    censusLabel: 'Records — Part C grievances universe',
    censusDesc:  'The case-level Part C grievance records your file was built from.',
    submissionDesc: 'The Part C Grievances .txt.',
  },
  'I.D': {
    measureId: 'grievances_d', schemaId: 'grievances-d',
    validateEndpoint: '/api/reporter/validate-canonical/grievances_d',
    recountEndpoint:  '/api/reporter/recount/grievances-d',
    fileShapeEndpoint:'/api/reporter/file-shape/grievances-d',
    buildEndpoint:    '/api/reporter/build/grievances_d',
    censusLabel: 'Records — Part D grievances universe',
    censusDesc:  'The case-level Part D grievance records your file was built from.',
    submissionDesc: 'The Part D Grievances .txt.',
  },
  'V.1.D': {
    measureId: 'enrollment_d', schemaId: 'enrollment-d',
    validateEndpoint: '/api/reporter/validate-canonical/enrollment_d',
    recountEndpoint:  '/api/reporter/recount/enrollment-d',
    fileShapeEndpoint:'/api/reporter/file-shape/enrollment-d',
    buildEndpoint:    '/api/reporter/build/enrollment_d',
    censusLabel: 'Records — Part D enrollment activity',
    censusDesc:  'One row per enrollment request, with received date, channel, outcome.',
    submissionDesc: 'The Part D Enrollment .txt.',
  },
  'V.2.D': {
    measureId: 'disenrollment_d', schemaId: 'disenrollment-d',
    validateEndpoint: '/api/reporter/validate-canonical/disenrollment_d',
    recountEndpoint:  '/api/reporter/recount/disenrollment-d',
    fileShapeEndpoint:'/api/reporter/file-shape/disenrollment-d',
    buildEndpoint:    '/api/reporter/build/disenrollment_d',
    censusLabel: 'Records — Part D disenrollment activity',
    censusDesc:  'One row per disenrollment request, voluntary and involuntary.',
    submissionDesc: 'The Part D Disenrollment .txt.',
  },
  'II.5.D': {
    measureId: 'reopenings_d', schemaId: 'reopenings-d',
    validateEndpoint: '/api/reporter/validate-canonical/reopenings_d',
    recountEndpoint:  '/api/reporter/recount/reopenings-d',
    fileShapeEndpoint:'/api/reporter/file-shape/reopenings-d',
    buildEndpoint:    '/api/reporter/build/reopenings_d',
    censusLabel: 'Records — Part D reopenings (within CDR)',
    censusDesc:  'Case-level re-opened coverage determination and redetermination records.',
    submissionDesc: 'The Part D Reopenings .txt (one row per re-opened case).',
  },
};

export default function ReporterSectionWork({ sectionId, sectionMode, onBack }) {
  const canonical = CANONICAL_DISPATCH[sectionId];
  if (canonical) {
    return (
      <CanonicalSectionWork
        sectionId={sectionId}
        canonical={canonical}
        sectionMode={sectionMode}
        onBack={onBack}
      />
    );
  }
  return <LegacySectionWork sectionId={sectionId} sectionMode={sectionMode} onBack={onBack} />;
}

// ---------------------------------------------------------------------------
// CountsConvertCard — Excel counts workbook → HPMS .TXT (Section V family).
// A vendor's answers usually arrive as "Element Letter / Element Name /
// Total" in Excel. HPMS takes a tab-delimited .TXT with no header. This
// card converts one to the other and preflights the GENERATED file, so a
// green result is the file you actually upload. The section (Part C vs D,
// enrollment vs disenrollment) is detected from the workbook itself.
// ---------------------------------------------------------------------------
function CountsConvertCard({ sectionId }) {
  const [file, setFile] = useState(null);
  const [contractId, setContractId] = useState('');
  const [year, setYear] = useState(2026);
  const [period, setPeriod] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [problems, setProblems] = useState(null);

  async function convert() {
    setBusy(true); setError(null); setProblems(null); setResult(null);
    try {
      if (!file) throw new Error('Drop in the Excel counts workbook.');
      if (!/^[HSRE]\d{4}$/i.test(contractId.trim())) throw new Error('Enter the Contract ID (H/S/R/E + 4 digits, e.g. H1234) — the workbook does not carry it.');
      const fd = new FormData();
      fd.append('workbook', file);
      fd.append('contractId', contractId.trim().toUpperCase());
      fd.append('year', String(year));
      fd.append('period', String(period));
      const res = await fetch(`${API}/api/reporter/convert/counts`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (!res.ok) { setProblems(data.problems || null); throw new Error(data.error || 'Conversion failed.'); }
      setResult(data);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function download() {
    if (!result?.txt) return;
    const bytes = Uint8Array.from(atob(result.txt.base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = result.txt.filename; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-white border border-slate-200 p-6 mb-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Only have the counts? Convert Excel → HPMS .TXT</h3>
      <p className="text-xs text-slate-500 mb-4">
        If your vendor handed you the Section V answers as a spreadsheet (Element Letter / Element Name / Total),
        drop it here. We detect which file it is (Part C or D, enrollment or disenrollment), build the
        tab-delimited .TXT exactly as the CMS File Record Layout requires, and run the full HPMS check on the
        generated file. What you download is what was checked.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <FileDrop label="Counts workbook (.xlsx) *" description="The vendor's element-summary spreadsheet."
          file={file} onChange={setFile} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Contract ID</label>
            <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="H1234" maxLength={5}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono uppercase" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Period</label>
            <select value={period} onChange={(e) => setPeriod(Number(e.target.value))}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono">
              <option value={1}>H1 (Jan–Jun) — due last Mon of Aug</option>
              <option value={2}>H2 (Jul–Dec) — due last Mon of Feb</option>
            </select>
          </div>
        </div>
      </div>
      <button onClick={convert} disabled={busy}
        className="bg-slate-900 hover:bg-slate-700 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold transition-colors">
        {busy ? 'Converting…' : 'Convert and check'}
      </button>
      {error && (
        <div className="mt-3 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 font-mono">
          {error}
          {problems && problems.map((p, i) => <div key={i} className="mt-1 text-xs">• {p}</div>)}
        </div>
      )}
      {result && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-xs font-mono px-2 py-1 border border-slate-300 bg-slate-50 text-slate-700">
              Detected: {result.detected.section} · Part {result.detected.part} · {result.detected.subsection}
              {' '}· {Object.keys(result.detected.elements).length} elements · sheet "{result.detected.sheet}"
            </span>
            <button onClick={download}
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 text-xs font-semibold">
              Download {result.txt.filename}
            </button>
          </div>
          <HpmsPreflightPanel preflight={result.hpmsPreflight} />
        </div>
      )}
    </div>
  );
}

function LegacySectionWork({ sectionId, sectionMode, onBack }) {
  const dispatch = SECTION_DISPATCH[sectionId] || SECTION_DISPATCH['IX'];
  const hasRecountPath = dispatch.needsCensus !== false;
  const initialMode = sectionMode === 'full' && hasRecountPath ? 'full' : 'preflight';
  const [mode, setMode] = useState(initialMode);

  // Files keyed by FileDrop key from the dispatch table.
  const [files, setFiles] = useState({});
  const setFile = (key, f) => setFiles(prev => ({ ...prev, [key]: f }));
  const primaryKey = dispatch.primaryFileKey || 'submission';
  const primaryFile = files[primaryKey] || null;
  const primarySpec = dispatch.files.find(f => f.key === primaryKey) || { label: 'HPMS file (.txt) *', desc: "The file you're about to send." };

  const [year, setYear] = useState(2026);
  const [period, setPeriod] = useState(1);
  const [part, setPart] = useState('C');

  // Section II.5 (Re-openings): the same three-area question Section II asks.
  // Reopenings span UM, Claims, and Appeals — each row's Case Level + Case
  // Type say which — so the declared answer is enforced case by case. Element
  // A (the HPMS data-entry count) is reconciled to the file's row count.
  const isReopenings = sectionId === 'II.5';
  const [reopenScope, setReopenScope] = useState([]);
  const toggleReopenScope = (a) => setReopenScope((s) => s.includes(a) ? s.filter((x) => x !== a) : [...s, a]);
  const [elementA, setElementA] = useState('');
  const REOPEN_SCOPE_OPTIONS = [
    { id: 'um', name: 'UM', desc: 'Reopened service determinations' },
    { id: 'claims', name: 'Claims', desc: 'Reopened payment determinations' },
    { id: 'appeals', name: 'Appeals', desc: 'Reopened reconsiderations' },
  ];

  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  async function run() {
    setError(null); setRunning(true); setReport(null);

    try {
      if (isReopenings && mode !== 'preflight' && reopenScope.length === 0) {
        throw new Error('Answer the question first: tick which areas this re-openings file covers — UM, Claims, or Appeals (one, two, or all three).');
      }
      let res;
      if (mode === 'preflight') {
        if (!primaryFile) throw new Error("Drop in the file you're about to send to HPMS.");
        const fd = new FormData();
        // The preflight endpoint expects the file under the field name 'submission'
        // regardless of section. For sections whose primary file has a different
        // backend name (e.g. II.5's reopeningsFile), we still send under 'submission'
        // for the preflight check.
        fd.append('submission', primaryFile);
        fd.append('section', sectionId);
        fd.append('year', String(year));
        fd.append('quarter', String(period));
        res = await fetch(`${API}/api/reporter/preflight`, {
          method: 'POST', credentials: 'include', body: fd,
        });
      } else {
        // Records-recount mode — dispatch per section.
        const required = dispatch.files.filter(f => !f.optional);
        const missing = required.filter(f => !files[f.key]);
        if (missing.length) {
          throw new Error(`Missing required file${missing.length > 1 ? 's' : ''}: ${missing.map(m => m.label).join(', ')}.`);
        }

        const fd = new FormData();
        for (const spec of dispatch.files) {
          const f = files[spec.key];
          if (f) fd.append(spec.key, f);
        }
        fd.append('year', String(year));
        fd.append('period', String(period));
        fd.append('quarter', String(period));
        fd.append('part', part);
        if (isReopenings) {
          fd.append('scope', reopenScope.join(','));
          if (String(elementA).trim() !== '') fd.append('elementA', String(elementA).trim());
        }

        const endpoint = dispatch.endpoint(sectionId, part);
        res = await fetch(`${API}${endpoint}`, { method: 'POST', credentials: 'include', body: fd });
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Validation failed');
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  const canRecount = hasRecountPath;

  return (
    <div>
      <button onClick={onBack} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4">
        ← All sections
      </button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1">§ {sectionId}</div>
          <h2 className="text-lg font-semibold text-slate-900">
            {sectionId} validation
          </h2>
        </div>
        {canRecount && (
          <div className="border border-slate-300 flex text-xs font-mono">
            <button
              onClick={() => setMode('preflight')}
              className={`px-3 py-1.5 transition-colors ${mode === 'preflight' ? 'bg-red-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              File check
            </button>
            <button
              onClick={() => setMode('full')}
              className={`px-3 py-1.5 transition-colors border-l border-slate-300 ${mode === 'full' ? 'bg-red-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              File check + Records recount
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">
          {mode === 'preflight' ? 'File check' : 'File check + Records recount'}
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          {mode === 'preflight'
            ? "Drop in the .txt you're about to send to HPMS. We tell you whether HPMS will accept it — with the CMS rule behind every issue."
            : "Drop in the .txt you're about to send AND the records you built it from. We recount the records, compare them to your file, and show you every miscount — with the CMS rule behind every finding — before you upload."}
        </p>

        {mode === 'full' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {dispatch.files.map(spec => (
              <FileDrop
                key={spec.key}
                label={spec.label + (spec.optional ? '' : ' *')}
                description={spec.desc}
                file={files[spec.key] || null}
                onChange={(f) => setFile(spec.key, f)}
                compact={spec.optional}
              />
            ))}
          </div>
        )}

        {mode === 'preflight' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FileDrop
              label={(primarySpec.label || 'HPMS file (.txt)').replace(/\s*\*$/, '') + ' *'}
              description={primarySpec.desc || "The file you're about to send. No records needed for the file check."}
              file={primaryFile}
              onChange={(f) => setFile(primaryKey, f)}
            />
          </div>
        )}

        {isReopenings && mode !== 'preflight' && (
          <div className={`mb-4 border-2 px-4 py-3 ${reopenScope.length === 0 ? 'border-red-300 bg-red-50/50' : 'border-emerald-300 bg-emerald-50/40'}`}>
            <div className="text-sm font-semibold text-slate-900">What does this re-openings file cover?</div>
            <p className="text-xs text-slate-600 mt-0.5 mb-2">
              Re-openings span all three Section II areas — every case's Case Level and Case Type say which area it belongs to.
              Tick everything this file includes; any case outside your answer is flagged.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              {REOPEN_SCOPE_OPTIONS.map((o) => {
                const on = reopenScope.includes(o.id);
                return (
                  <button type="button" key={o.id} onClick={() => toggleReopenScope(o.id)}
                    className={`flex-1 text-left border px-3 py-2 transition-colors ${on ? 'border-emerald-600 bg-emerald-600/10' : 'border-slate-300 bg-white hover:bg-slate-50'}`}>
                    <div className={`text-sm font-semibold ${on ? 'text-emerald-800' : 'text-slate-800'}`}>{on ? '☑' : '☐'} {o.name}</div>
                    <div className="text-[11px] text-slate-500">{o.desc}</div>
                  </button>
                );
              })}
            </div>
            <div className={`text-[12px] mt-2 ${reopenScope.length === 0 ? 'text-red-700 font-semibold' : 'text-emerald-800'}`}>
              {reopenScope.length === 0 ? 'Required — nothing runs until you answer.' : `This file covers: ${reopenScope.map((a) => REOPEN_SCOPE_OPTIONS.find((o) => o.id === a)?.name).join(' + ')}.`}
            </div>
            <div className="mt-3 max-w-xs">
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Element A — reopened cases (HPMS data entry)</label>
              <input type="number" min="0" value={elementA} onChange={(e) => setElementA(e.target.value)}
                placeholder="e.g. 12"
                className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
              <div className="text-[11px] text-slate-500 mt-1">The count you will type into HPMS. The tool checks it equals the number of cases in your file.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-4 max-w-xl">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Year</label>
            <input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Period</label>
            <select value={period} onChange={e => setPeriod(Number(e.target.value))}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono">
              <option value={1}>Period 1 / Q1</option>
              <option value={2}>Period 2 / Q2</option>
              <option value={3}>Q3</option>
              <option value={4}>Q4 / Annual</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Part</label>
            <select value={part} onChange={e => setPart(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono">
              <option value="C">Part C</option>
              <option value="D">Part D</option>
            </select>
          </div>
        </div>

        <button onClick={run} disabled={running}
          className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold transition-colors">
          {running ? 'Checking…' : (mode === 'preflight' ? 'Run file check' : 'Run file check + recount')}
        </button>

        {error && (
          <div className="mt-3 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 font-mono">{error}</div>
        )}

        {/* Agent activity for the in-flight reporter validation run. Renders
            nothing until the validation agent (HPMS preflight, derived
            counts, reconciliation) emits events. */}
        {report?.runId && <ReporterRunAgentActivity runId={report.runId} />}
      </div>

      {/* Section V only: the vendor-summary Excel → HPMS .TXT converter. */}
      {/^V\.[12]/.test(sectionId) && <CountsConvertCard sectionId={sectionId} />}

      {report && (isReopenings && mode !== 'preflight' && report.verdict
        ? <ReopeningsReportPanel report={report} />
        : sectionId === 'IV' && mode !== 'preflight' && report.verdict
          ? <SnpReportPanel report={report} />
          : <ReportPanel report={report} mode={mode} sectionId={sectionId}/>)}
    </div>
  );
}

// Section IV (SNP Care Management) result: the verdict and stage ladder first,
// then the arithmetic with its numbers, the per-plan derived counts, every
// difference with the enrollees behind it, and the formulas one click away.
function SnpReportPanel({ report }) {
  const { verdict, counts_check, findings = [], derived, hpmsPreflight, warnings = [] } = report;
  return (
    <div className="bg-white border border-slate-200 p-6 mt-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">SNP Care Management check result</h3>

      <div className={`px-4 py-3 border-t-4 mb-4 ${verdict.ready ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
        <div className={`text-[15px] font-bold ${verdict.ready ? 'text-emerald-900' : 'text-red-900'}`}>{verdict.headline}</div>
        {(verdict.stages || []).length > 0 && (
          <div className="mt-2 space-y-1">
            {verdict.stages.map((s, i) => (
              <div key={i} className={`text-[13px] ${s.ok ? 'text-emerald-800' : 'text-red-900'}`}>
                <span className="font-bold">{s.ok ? '✓' : '✗'} {s.label}: </span>{s.text}
              </div>
            ))}
          </div>
        )}
        {(verdict.blockers || []).length > 0 && (
          <ol className="mt-2 space-y-1 text-[13px] text-red-900 list-decimal list-inside">
            {verdict.blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ol>
        )}
        {(verdict.manual_checks || []).length > 0 && (
          <details className="mt-2 text-[12.5px] text-slate-700">
            <summary className="cursor-pointer font-semibold">Guidance rules only your documentation can prove — confirm before filing ({verdict.manual_checks.length})</summary>
            {verdict.manual_checks_intro && <div className="mt-1 mb-1 text-slate-600">{verdict.manual_checks_intro}</div>}
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {verdict.manual_checks.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </details>
        )}
      </div>

      <SnpFormulas />

      {(verdict.unverifiable || []).length > 0 && (
        <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 text-[13px] text-amber-900 space-y-1.5">
          {verdict.unverifiable.map((g, i) => (
            <div key={i}><span className="font-semibold">Could not be checked: </span>{g.reason}</div>
          ))}
        </div>
      )}

      {counts_check && (
        <section className={`mb-4 border-2 ${counts_check.passed ? 'border-emerald-300' : 'border-red-300'}`}>
          <div className={`px-4 py-3 ${counts_check.passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counts check</div>
            <div className={`text-sm font-bold ${counts_check.passed ? 'text-emerald-800' : 'text-red-800'}`}>
              {counts_check.passed
                ? `COUNTS CHECK PASSED — the figures add up (${counts_check.checks.length} arithmetic check${counts_check.checks.length === 1 ? '' : 's'}).`
                : 'COUNTS CHECK FAILED — the arithmetic does not add up.'}
            </div>
          </div>
          <div className="px-4 py-2 space-y-1">
            {counts_check.checks.map((c, i) => (
              <div key={i} className={`text-[13px] ${c.ok ? 'text-emerald-800' : 'text-red-900'}`}>
                <span className="font-semibold">{c.ok ? '✓' : '✗'} {c.label}: </span>{c.detail}
              </div>
            ))}
          </div>
        </section>
      )}

      {derived?.byPbp?.length > 0 && (
        <section className="mb-4">
          <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Derived counts from your census</h4>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2">Plan</th>
                {['A','B','C','D','E','F','G','H'].map((e) => <th key={e} className="text-right py-2 w-14">{e}</th>)}
              </tr>
            </thead>
            <tbody>
              {derived.byPbp.map((g, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1 font-mono">{g.contract} · {g.pbp}</td>
                  {['A','B','C','D','E','F','G','H'].map((e) => <td key={e} className="py-1 text-right font-mono">{g.counts[e]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {findings.length > 0 && (
        <div className="space-y-3">
          <div className="text-[13px] font-semibold text-amber-800">✗ {findings.length} difference{findings.length === 1 ? '' : 's'}:</div>
          {findings.map((f, i) => (
            <div key={i} className="border border-amber-200 bg-amber-50/50 px-3.5 py-3 text-sm text-slate-800">
              <div className="font-semibold text-slate-900">{f.label || f.message}</div>
              {f.plain && <div className="mt-1 text-[13.5px] font-semibold text-amber-900">{f.plain}</div>}
              {f.where && <div className="mt-2 text-[13px]"><span className="font-semibold text-slate-700">Where: </span>{f.where}</div>}
              {f.remedy && <div className="mt-1.5 text-[13px] text-slate-700"><span className="font-semibold">What to do: </span>{f.remedy}</div>}
            </div>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="mt-4 space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="border-l-4 border-amber-400 bg-amber-50 px-3 py-1.5 text-[13px] text-slate-800">{w}</div>
          ))}
        </div>
      )}

      {hpmsPreflight && <div className="mt-4"><HpmsPreflightPanel preflight={hpmsPreflight} /></div>}
    </div>
  );
}

// The Section IV formulas, verbatim from the CY2026 layout and technical
// specifications — expandable next to the numbers they explain.
function SnpFormulas() {
  return (
    <details className="mb-4 border border-slate-300 bg-slate-50/60 px-4 py-2.5">
      <summary className="cursor-pointer text-sm font-semibold text-slate-800">
        The formulas — how every number is calculated (per the CY2026 SNP guidance)
      </summary>
      <div className="mt-2 text-[13px] text-slate-700 space-y-2">
        <div><span className="font-semibold">The clock:</span> the effective enrollment date is <b>day 0</b>. The initial HRA window is the <b>90 days before or after</b> it. The reassessment cycle is <b>365 days</b>, counted from the day after the last HRA (or from enrollment when no HRA was ever done). Annual measure: one measurement year, one record per plan (Contract + PBP).</div>
        <div>
          <ul className="list-disc list-inside space-y-0.5">
            <li><b>A</b> = new enrollees whose enrollment falls in the year and who stayed enrolled at least 90 days (or completed an initial HRA sooner) — plus prior-year enrollees whose 90-day deadline lands in this year with no initial HRA done.</li>
            <li><b>C</b> = of A, initial HRAs completed <b>inside the ±90-day window</b>. An HRA before the enrollment date counts in the enrollment date's year. An HRA outside the window is not initial — it becomes a reassessment.</li>
            <li><b>D / E</b> = of A with no completed initial HRA: documented refusal (D) or documented unable-to-reach (E).</li>
            <li><b>B</b> = enrollees whose 365-day reassessment deadline lands in the year (or who completed a reassessment in it). An enrollee can legitimately be in both A and B.</li>
            <li><b>F</b> = of B, the most recent reassessment completed <b>within 365 days</b> of its anchor. A later one is non-compliant — not counted, but it resets the next cycle.</li>
            <li><b>G / H</b> = of B with no compliant reassessment: documented refusal (G) or unable-to-reach (H).</li>
            <li>An enrollee is counted <b>at most once per element</b>; the most recent activity governs.</li>
            <li>So <b>C + D + E ≤ A</b> and <b>F + G + H ≤ B</b> — the gap, if any, is enrollees with no completed HRA and no documented outcome (reported in A or B alone).</li>
          </ul>
        </div>
      </div>
    </details>
  );
}

// Section II.5 result: the verdict first, then every problem with the exact
// case, the verbatim value, and the fix — nothing generic.
function ReopeningsReportPanel({ report }) {
  const { verdict, rowFlags = [], scopeConflicts = [], reconciliation = {}, orphanCaseIds = [], rowCount } = report;
  const sevRank = { critical: 0, high: 1, medium: 2 };
  const flags = [...rowFlags].sort((a, b) => (sevRank[a.severity] ?? 3) - (sevRank[b.severity] ?? 3));
  return (
    <div className="bg-white border border-slate-200 p-6 mt-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Re-openings check result</h3>

      <div className={`px-4 py-3 border-t-4 mb-4 ${verdict.ready ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
        <div className={`text-[15px] font-bold ${verdict.ready ? 'text-emerald-900' : 'text-red-900'}`}>{verdict.headline}</div>
        {(verdict.blockers || []).length > 0 && (
          <ol className="mt-2 space-y-1 text-[13px] text-red-900 list-decimal list-inside">
            {verdict.blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ol>
        )}
        {(verdict.manual_checks || []).length > 0 && (
          <details className="mt-2 text-[12.5px] text-slate-700">
            <summary className="cursor-pointer font-semibold">Required exclusions — these must NOT be in this file; confirm your export left them out ({verdict.manual_checks.length})</summary>
            {verdict.manual_checks_intro && (
              <div className="mt-1 mb-1 text-slate-600">{verdict.manual_checks_intro}</div>
            )}
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {verdict.manual_checks.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </details>
        )}
      </div>

      <div className="text-[13px] text-slate-600 mb-3">
        Read <b>{rowCount}</b> reopened case{rowCount === 1 ? '' : 's'} from the file
        {reconciliation.submittedA != null && <> · Element A says <b>{reconciliation.submittedA}</b>{reconciliation.reconciles ? ' — matches' : ` — does NOT match (off by ${Math.abs(reconciliation.delta)})`}</>}.
      </div>

      {scopeConflicts.length > 0 && (
        <div className="mb-3 px-4 py-3 bg-red-50 border-2 border-red-300">
          <div className="text-[13px] font-bold text-red-800 mb-2">⚠ The file does not match your answer about what it covers:</div>
          <div className="space-y-2">
            {scopeConflicts.map((c, i) => <div key={i} className="border border-red-200 bg-white px-3 py-2 text-sm text-slate-800">{c.message}</div>)}
          </div>
        </div>
      )}

      {flags.length > 0 && (
        <div className="space-y-2">
          {flags.map((f, i) => (
            <div key={i} className={`border-l-4 px-3 py-2 text-sm text-slate-800 ${f.severity === 'critical' ? 'border-red-700 bg-red-50' : f.severity === 'high' ? 'border-amber-600 bg-amber-50' : 'border-slate-300 bg-slate-50'}`}>
              <span className={`text-[10px] font-mono uppercase mr-2 ${f.severity === 'critical' ? 'text-red-700' : f.severity === 'high' ? 'text-amber-700' : 'text-slate-500'}`}>{f.severity}</span>
              {f.message}
            </div>
          ))}
        </div>
      )}

      {orphanCaseIds.length > 0 && (
        <details className="mt-3 text-[12.5px] text-slate-600">
          <summary className="cursor-pointer font-semibold">Cases not found in your OD/RECON records ({orphanCaseIds.length})</summary>
          <ul className="list-disc list-inside mt-1">
            {orphanCaseIds.map((o, i) => <li key={i}>{o.caseId} (row {o.rowIndex}) — {o.note}</li>)}
          </ul>
        </details>
      )}

      {verdict.ready && flags.length === 0 && scopeConflicts.length === 0 && (
        <div className="text-sm text-emerald-800">Every case passed every check: layout values, date order, quarter placement, your declared scope, and the Element A count.</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CanonicalSectionWork — workspace for measures served by the canonical
// engine registry. Three modes:
//   'preflight' — file-shape check only          (submission → /file-shape)
//   'full'      — file shape + recount + reconcile (submission + census → /validate-canonical)
//   'recount'   — records recount only            (census → /recount)
// Plus a "Build submission file" action that POSTs the census to
// /api/reporter/build/:measureId and downloads the returned .TXT.
// ---------------------------------------------------------------------------
const CANONICAL_MODES = [
  { id: 'recount',   label: 'Read my counts (Excel or .txt)' },
  { id: 'preflight', label: 'Check HPMS .txt' },
  { id: 'full',      label: 'Counts + Records' },
];

function CanonicalSectionWork({ sectionId, canonical, sectionMode, onBack }) {
  // Default to reading the user's counts (Excel or .txt). File-shape checking of
  // the HPMS .txt is a deliberate, secondary choice — not the trap you land in.
  // Section II opens on the combined check — counts and records side by side —
  // because "do my counts match my records?" is the question people come with.
  const [mode, setMode] = useState(canonical.measureId === 'odr' ? 'full' : (sectionMode === 'full' ? 'full' : 'recount'));
  // Section II (ODR) mixes three areas that submit separately. The tool asks
  // what THIS upload covers — one, two, or all three — and nothing runs until
  // the user answers. No default: an unanswered question is not "all".
  const isODR = canonical.measureId === 'odr';
  const [odrScope, setOdrScope] = useState([]);
  const toggleScope = (a) => setOdrScope((s) => s.includes(a) ? s.filter((x) => x !== a) : [...s, a]);
  const ODR_SCOPE_OPTIONS = [
    { id: 'um', name: 'UM', desc: 'Service determinations (pre-service organization determinations)' },
    { id: 'claims', name: 'Claims', desc: 'Payment determinations (claims organization determinations)' },
    { id: 'appeals', name: 'Recon (Appeals)', desc: 'Reconsiderations (first-level appeals)' },
    { id: 'reopenings', name: 'Re-openings', desc: 'Revised decisions — a separate case-level file, checked in Section II.5' },
  ];
  const SCOPE_PROMPT = 'Answer the question first: tick which areas this upload covers — UM, Claims, Recon, or all.';
  // Re-openings are their own HPMS file (Section II.5) per the CY2026 layouts;
  // the tick here is a signpost, not a filter this file's engine applies.
  const sendScope = () => odrScope.filter((a) => a !== 'reopenings');
  const reopeningsTicked = odrScope.includes('reopenings');
  const scopeMissing = isODR && sendScope().length === 0;
  const [submission, setSubmission] = useState(null);
  const [census, setCensus] = useState(null);
  const [year, setYear] = useState(2026);
  // Reporting period: annual, a quarter, a half, or specific month(s).
  const [periodSel, setPeriodSel] = useState('annual');
  const [monthSel, setMonthSel] = useState([]); // [1..12] when periodSel === 'months'

  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [errorDiag, setErrorDiag] = useState(null);
  const [notice, setNotice] = useState(null);
  // Step two: reconcile the read counts against the case-level census.
  const [reconcileCensus, setReconcileCensus] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [reconcileError, setReconcileError] = useState(null);
  // Step three: export the records with row-level errors, and submit as final.
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState(null); // { kind, text }
  const [submitting, setSubmitting] = useState(false);
  const [submitNote, setSubmitNote] = useState(null); // { kind, text }

  // A spreadsheet (Excel/CSV) is a counts file, not the HPMS .txt. If the user
  // drops one into a slot that expects the .txt, quietly do the right thing:
  // treat it as the records/counts file and switch to the recount reader.
  const isSpreadsheet = (f) => f && /\.(xlsx|xls|csv)$/i.test(f.name || '');
  const onPickSubmission = (file) => {
    setNotice(null); setError(null); setErrorDiag(null);
    // In the combined check a spreadsheet in the counts slot is simply the
    // COUNTS workbook — it stays put and reconciles against the records. The
    // old behavior (silently switching modes and discarding the census) made
    // the workspace impossible to follow.
    if (isSpreadsheet(file) && mode === 'full') {
      setSubmission(file);
      setNotice('Reading this spreadsheet as your COUNTS workbook. With your case records in the other slot, Run compares the two number by number; with just this file, Run reads the counts.');
      return;
    }
    if (isSpreadsheet(file)) {
      setCensus(file); setSubmission(null); setMode('recount');
      setNotice('That is a spreadsheet with your counts, so I switched to “Records recount,” which reads the counts directly from it. Pick your period below and click Run.');
      return;
    }
    setSubmission(file);
  };

  // Append the reporting period to a request: year alone means annual; a
  // quarter sends `quarter`; a half sends the legacy `period`; month(s) send a
  // comma list the tool sums (a summary file's month columns, or a date range).
  const appendPeriod = (fd) => {
    if (periodSel === 'months') fd.append('months', monthSel.join(','));
    else if (periodSel.startsWith('q')) fd.append('quarter', periodSel.slice(1));
    else if (periodSel === 'h1') fd.append('period', '1');
    else if (periodSel === 'h2') fd.append('period', '2');
    // 'annual' → nothing; the backend defaults to the full contract year.
  };
  const toggleMonth = (m) => setMonthSel(s => s.includes(m) ? s.filter(x => x !== m) : [...s, m].sort((a, b) => a - b));

  const [building, setBuilding] = useState(false);
  const [buildNote, setBuildNote] = useState(null); // { kind: 'ok'|'info'|'error', text }

  const needsSubmission = mode !== 'recount';
  const needsCensus = mode !== 'preflight';

  async function run() {
    setError(null); setErrorDiag(null); setRunning(true); setReport(null);
    try {
      if (periodSel === 'months' && mode !== 'preflight' && !monthSel.length) {
        throw new Error('Pick at least one month, or choose a different reporting period.');
      }
      if (scopeMissing && mode !== 'preflight') throw new Error(SCOPE_PROMPT);
      let res;
      if (mode === 'preflight') {
        if (!submission) throw new Error("Drop in the .txt you're about to send to HPMS.");
        const fd = new FormData();
        fd.append('submission', submission);
        fd.append('cy', String(year));
        res = await fetch(`${API}${canonical.fileShapeEndpoint}`, {
          method: 'POST', credentials: 'include', body: fd,
        });
      } else if (mode === 'recount') {
        if (!census) throw new Error('Drop in the records (census) file to recount.');
        const fd = new FormData();
        fd.append('census', census);
        fd.append('year', String(year));
        if (isODR) fd.append('scope', sendScope().join(','));
        appendPeriod(fd);
        res = await fetch(`${API}${canonical.recountEndpoint}`, {
          method: 'POST', credentials: 'include', body: fd,
        });
      } else {
        // Section II combined check: EITHER file alone runs on its own (the
        // reader tells counts from records automatically); both together
        // reconcile number against number.
        if (isODR && (!submission || !census)) {
          const only = submission || census;
          if (!only) throw new Error('Add your counts, your case records, or both.');
          const fd = new FormData();
          fd.append('census', only);
          fd.append('year', String(year));
          fd.append('scope', sendScope().join(','));
          appendPeriod(fd);
          res = await fetch(`${API}${canonical.recountEndpoint}`, {
            method: 'POST', credentials: 'include', body: fd,
          });
          let data;
          try { data = await res.json(); }
          catch { throw new Error(`Unexpected response from the server (${res.status}).`); }
          if (!res.ok) { setErrorDiag(data.diagnostics || null); throw new Error(data.error || 'Validation failed'); }
          setNotice(null);
          setReport({ mode: 'recount', data });
          return;
        }
        if (!submission || !census) {
          throw new Error('Full validation needs both files: the HPMS .txt AND the records it was built from.');
        }
        const fd = new FormData();
        fd.append('submission', submission);
        fd.append('census', census);
        fd.append('year', String(year));
        fd.append('cy', String(year));
        if (isODR) fd.append('scope', sendScope().join(','));
        appendPeriod(fd);
        res = await fetch(`${API}${canonical.validateEndpoint}`, {
          method: 'POST', credentials: 'include', body: fd,
        });
      }

      let data;
      try { data = await res.json(); }
      catch { throw new Error(`Unexpected response from the server (${res.status}).`); }
      if (!res.ok) {
        // The server recognized a spreadsheet in a .txt slot — switch to the
        // counts reader and carry the file over, instead of a dead-end error.
        if (data.wrong_mode && data.suggest_mode === 'recount') {
          if (submission) { setCensus(submission); setSubmission(null); }
          setMode('recount');
          setNotice(`${data.error} I switched you to “Records recount” — pick your period and click Run.`);
          return;
        }
        // Keep the structured diagnostics so the failure panel can show exactly
        // what went wrong (which columns, which values, rows read vs counted).
        setErrorDiag(data.diagnostics || null);
        throw new Error(data.error || 'Validation failed');
      }
      setNotice(null);
      setReport({ mode, data });
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  }

  async function buildSubmission() {
    setBuildNote(null);
    if (!census) {
      setBuildNote({ kind: 'error', text: 'Drop in the records (census) file first. The builder generates the .TXT from your records.' });
      return;
    }
    if (scopeMissing) { setBuildNote({ kind: 'error', text: SCOPE_PROMPT }); return; }
    setBuilding(true);
    try {
      const fd = new FormData();
      fd.append('census', census);
      fd.append('year', String(year));
      fd.append('cy', String(year));
      if (isODR) fd.append('scope', sendScope().join(','));
      appendPeriod(fd);
      const res = await fetch(`${API}${canonical.buildEndpoint}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (res.status === 404) {
        setBuildNote({ kind: 'info', text: 'Builder not available yet for this measure.' });
        return;
      }
      if (!res.ok) {
        let msg = `Build failed (${res.status}).`;
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch { /* keep msg */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename\*?="?([^";]+)"?/i);
      const name = (m && m[1]) || `${canonical.schemaId.toUpperCase().replace(/-/g, '_')}_CY${year}.TXT`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setBuildNote({ kind: 'ok', text: `Downloaded ${name}.` });
    } catch (e) {
      setBuildNote({ kind: 'error', text: e.message });
    } finally {
      setBuilding(false);
    }
  }

  // Step two: send the counts file (read in step one) AND the case-level census
  // to the reconcile endpoint; it runs the engine on each and compares them.
  async function runReconcile() {
    setReconcileError(null); setReconcileResult(null); setReconciling(true);
    try {
      if (!census) throw new Error('Read the counts first, then add your records to reconcile.');
      if (!reconcileCensus) throw new Error('Add your case-level records (census) to reconcile against the counts.');
      if (scopeMissing) throw new Error(SCOPE_PROMPT);
      const fd = new FormData();
      fd.append('counts', census);          // the counts file from step one
      fd.append('census', reconcileCensus); // the case-level records
      fd.append('year', String(year));
      if (isODR) fd.append('scope', sendScope().join(','));
      appendPeriod(fd);
      const res = await fetch(`${API}/api/reporter/reconcile/${canonical.measureId}`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ? `${data.stage === 'census' ? 'Records: ' : data.stage === 'counts' ? 'Counts: ' : ''}${data.error}` : 'Reconciliation failed');
      setReconcileResult(data);
    } catch (e) { setReconcileError(e.message); }
    finally { setReconciling(false); }
  }

  // A human label for the selected period, used when recording a submission.
  function periodLabelText() {
    if (periodSel === 'annual') return `CY${year} (full year)`;
    if (periodSel.startsWith('q')) return `Q${periodSel.slice(1)} ${year}`;
    if (periodSel === 'h1') return `First half ${year}`;
    if (periodSel === 'h2') return `Second half ${year}`;
    if (periodSel === 'months') {
      const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthSel.map(m => names[m - 1]).join(', ')} ${year}`;
    }
    return `CY${year}`;
  }

  // Step three: export the case-level records annotated with a per-row status
  // and a plain sentence saying what each problem row needs. When the counts
  // file from step one is present, the export also carries a reconciliation tab.
  async function exportErrors() {
    setExportNote(null); setExporting(true);
    try {
      const records = reconcileCensus || census;
      if (!records) throw new Error('Add your case-level records first, then export the annotated file.');
      const fd = new FormData();
      fd.append('census', records);
      if (reconcileCensus && census) fd.append('counts', census); // records + summary counts → reconciliation tab
      fd.append('year', String(year));
      if (isODR && sendScope().length) fd.append('scope', sendScope().join(','));
      appendPeriod(fd);
      const res = await fetch(`${API}/api/reporter/export-errors/${canonical.measureId}`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) {
        let msg = `Export failed (${res.status}).`;
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch { /* keep msg */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename\*?="?([^";]+)"?/i);
      const name = (m && m[1]) || `${canonical.measureId}_row_errors_${year}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setExportNote({ kind: 'ok', text: `Downloaded ${name}. Every row carries its status and, where it failed, what to fix.` });
    } catch (e) { setExportNote({ kind: 'error', text: e.message }); }
    finally { setExporting(false); }
  }

  // Record this result as the plan's final submission. Stores the aggregate
  // outcome only — no case rows leave the browser — and drops it into the
  // Submissions register for a compliance reviewer to accept or send back.
  async function submitAsFinal() {
    setSubmitNote(null); setSubmitting(true);
    try {
      const rec = reconcileResult?.reconciliation || null;
      const hadErrors = rec ? !rec.match : false;
      const errorCount = rec ? (rec.findings || []).length : 0;
      const body = {
        measure_id: canonical.measureId,
        measure_label: sectionId,
        reporting_year: year,
        period_label: periodLabelText(),
        had_errors: hadErrors,
        error_count: errorCount,
        counts: reconcileResult?.counts?.total || report?.data?.derived?.total || report?.data?.counts?.total || {},
        result: {
          reconciled: !!rec,
          reconciliation: rec ? { summary: rec.summary, match: rec.match, checked: rec.checked, matched: rec.matched, findings: rec.findings, census: rec.census, notes: rec.notes, scope: rec.scope || null } : null,
          scope: isODR ? { areas: sendScope(), label: rec?.scope?.label || sendScope().join(',') } : null,
        },
        counts_filename: census?.name || null,
        census_filename: reconcileCensus?.name || null,
      };
      const res = await fetch(`${API}/api/reporter/submissions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Could not record the submission (${res.status}).`);
      setSubmitNote({ kind: 'ok', text: `Recorded as final for ${periodLabelText()}${hadErrors ? ` with ${errorCount} open discrepanc${errorCount === 1 ? 'y' : 'ies'}` : ' — clean'}. It is now in the Submissions tab for compliance review.` });
    } catch (e) { setSubmitNote({ kind: 'error', text: e.message }); }
    finally { setSubmitting(false); }
  }

  const modeCopy = {
    preflight: "Drop in the .txt you're about to send to HPMS. We check every row, position, and element against the CMS file record layout and tell you whether HPMS will accept it.",
    full: isODR
      ? 'Upload your COUNTS (the tallied numbers — a vendor workbook or the finished .txt), your case RECORDS (one row per case), or both. One file alone gets checked on its own; both together get compared number by number, and the verdict says in plain words whether your counts match your records.'
      : "Drop in the .txt you're about to send AND the records you built it from. We check the file shape, recount the records, compare them to your file, and show you every miscount before you upload.",
    recount: 'Drop in your counts here — either your case-level records (census) OR a spreadsheet where the counts are already tallied (the CMS summary layout, by month/quarter). The tool detects which, reads the counts for the period you pick, checks that the totals add up, and shows the numbers your .txt should contain.',
  };

  return (
    <div>
      <button onClick={onBack} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4">
        ← All sections
      </button>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1">§ {sectionId}</div>
          <h2 className="text-lg font-semibold text-slate-900">{sectionId} validation</h2>
          <div className="text-xs font-mono text-slate-500 mt-1">Canonical engine · measure {canonical.measureId}</div>
        </div>
        <div className="border border-slate-300 flex text-xs font-mono">
          {CANONICAL_MODES.map((m, i) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`px-3 py-1.5 transition-colors ${i > 0 ? 'border-l border-slate-300' : ''} ${mode === m.id ? 'bg-red-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">
          {CANONICAL_MODES.find(m => m.id === mode)?.label}
        </h3>
        <p className="text-xs text-slate-500 mb-4">{modeCopy[mode]}</p>

        {isODR && mode !== 'preflight' && (
          <div className={`mb-4 border-2 px-4 py-3 ${scopeMissing ? 'border-red-300 bg-red-50/50' : 'border-emerald-300 bg-emerald-50/40'}`}>
            <div className="text-sm font-semibold text-slate-900">What does this upload cover?</div>
            <p className="text-xs text-slate-600 mt-0.5 mb-2">
              Section II combines three areas that usually submit separately. Tick everything this upload includes — one, two, or all three.
              The tool reviews and reconciles only what you tick, and flags anything in the file that falls outside your answer.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              {ODR_SCOPE_OPTIONS.map((o) => {
                const on = odrScope.includes(o.id);
                return (
                  <button type="button" key={o.id} onClick={() => toggleScope(o.id)}
                    className={`flex-1 text-left border px-3 py-2 transition-colors ${on ? 'border-emerald-600 bg-emerald-600/10' : 'border-slate-300 bg-white hover:bg-slate-50'}`}>
                    <div className={`text-sm font-semibold ${on ? 'text-emerald-800' : 'text-slate-800'}`}>
                      {on ? '☑' : '☐'} {o.name}
                    </div>
                    <div className="text-[11px] text-slate-500">{o.desc}</div>
                  </button>
                );
              })}
            </div>
            <div className={`text-[12px] mt-2 ${scopeMissing ? 'text-red-700 font-semibold' : 'text-emerald-800'}`}>
              {scopeMissing
                ? 'Required — nothing runs until you answer.'
                : `This upload covers: ${sendScope().map((a) => ODR_SCOPE_OPTIONS.find((o) => o.id === a)?.name).join(' + ')}${sendScope().length === 3 ? ' (everything in this HPMS file)' : ''}.`}
            </div>
            {reopeningsTicked && (
              <div className="text-[12px] mt-1 text-amber-800">
                Re-openings are their own case-level file per the CY2026 layouts — they never ride in this HPMS file.
                Check them under <b>Section II.5 (Reopenings)</b> from All sections; the same UM/Claims/Recon answer applies there.
              </div>
            )}
          </div>
        )}

        {isODR && mode === 'full' && (
          <div className="text-[12px] text-slate-600 mb-2">
            Upload <b>either file alone</b> to check it, or <b>both together</b> to compare them number by number.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {needsSubmission && isODR && mode === 'full' && (
            <FileDrop
              label="1 · COUNTS — tallied numbers (Excel workbook or HPMS .txt)"
              description="The counts as tallied — your vendor's workbook or the finished .txt."
              file={submission}
              onChange={onPickSubmission}
            />
          )}
          {needsCensus && (
            <FileDrop
              label={isODR && mode === 'full' ? '2 · RECORDS (census) — one row per case' : `${canonical.censusLabel} *`}
              description={canonical.censusDesc}
              file={census}
              onChange={setCensus}
            />
          )}
          {needsSubmission && !(isODR && mode === 'full') && (
            <FileDrop
              label="HPMS file (.txt) *"
              description={`${canonical.submissionDesc} — if you drop a spreadsheet here, it reads it as your counts instead.`}
              file={submission}
              onChange={onPickSubmission}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 max-w-md">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Year</label>
            <input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Reporting period</label>
            <select value={periodSel} onChange={e => setPeriodSel(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1 text-sm font-mono">
              <option value="annual">Annual (full year)</option>
              <option value="q1">Q1 (Jan–Mar)</option>
              <option value="q2">Q2 (Apr–Jun)</option>
              <option value="q3">Q3 (Jul–Sep)</option>
              <option value="q4">Q4 (Oct–Dec)</option>
              <option value="h1">First half (Jan–Jun)</option>
              <option value="h2">Second half (Jul–Dec)</option>
              <option value="months">Specific month(s)…</option>
            </select>
          </div>
        </div>

        {periodSel === 'months' && (
          <div className="mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Pick one or more months</div>
            <div className="flex flex-wrap gap-1.5">
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((mn, i) => {
                const m = i + 1; const on = monthSel.includes(m);
                return (
                  <button type="button" key={m} onClick={() => toggleMonth(m)}
                    className={`px-2.5 py-1 text-xs font-mono border ${on ? 'bg-red-700 text-white border-red-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                    {mn}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {monthSel.length ? `Counting ${monthSel.length} month${monthSel.length === 1 ? '' : 's'}. The tool sums exactly these months.` : 'Select at least one month.'}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button onClick={run} disabled={running}
            className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold transition-colors">
            {running ? 'Working…' : (mode === 'preflight' ? 'Check the .txt' : mode === 'recount' ? 'Read the counts'
              : (isODR ? (submission && census ? 'Compare counts to records' : 'Check what I uploaded') : 'Check .txt + read counts'))}
          </button>
          <button onClick={buildSubmission} disabled={building}
            className="border border-red-700 text-red-700 hover:bg-red-50 disabled:border-slate-300 disabled:text-slate-400 px-4 py-2 text-sm font-semibold transition-colors">
            {building ? 'Generating…' : 'Generate HPMS .txt'}
          </button>
        </div>

        {notice && (
          <div className="mt-3 border border-sky-300 bg-sky-50 text-sky-900 text-sm px-3 py-2">{notice}</div>
        )}
        {error && (
          <div className="mt-3 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>
        )}
        {errorDiag && <FailureDiagnostics diag={errorDiag} />}
        {buildNote && (
          <div className={`mt-3 border text-sm px-3 py-2 font-mono ${
            buildNote.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' :
            buildNote.kind === 'info' ? 'border-sky-200 bg-sky-50 text-sky-800' :
            'border-red-200 bg-red-50 text-red-800'
          }`}>{buildNote.text}</div>
        )}
      </div>

      {/* Section V only: the vendor-summary Excel → HPMS .TXT converter. */}
      {/^V\.[12]/.test(sectionId) && <CountsConvertCard sectionId={sectionId} />}

      {report && <CanonicalReportPanel report={report} />}

      {report && mode === 'recount' && (
        <div className="bg-white border border-slate-200 p-6 mt-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Step 2 · Reconcile against your records</h3>
          <p className="text-xs text-slate-500 mb-4">Optional. Upload the case-level records the counts were built from. The tool derives the counts from those records and checks, element by element, whether they match the counts above.</p>
          <FileDrop
            label="Case-level records (census)"
            description="Your detail/universe file for this measure (Excel, CSV, or .txt)."
            file={reconcileCensus}
            onChange={(f) => { setReconcileCensus(f); setReconcileResult(null); setReconcileError(null); }}
          />
          <div className="flex items-center gap-3 mt-4">
            <button onClick={runReconcile} disabled={reconciling || !reconcileCensus}
              className="bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold transition-colors">
              {reconciling ? 'Reconciling…' : 'Reconcile with the counts'}
            </button>
          </div>
          {reconcileError && <div className="mt-3 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{reconcileError}</div>}
          {reconcileResult && <ReconcileResult data={reconcileResult} />}
        </div>
      )}

      {report && mode === 'recount' && (
        <div className="bg-white border border-slate-200 p-6 mt-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Step 3 · Export the errors, then submit as final</h3>
          <p className="text-xs text-slate-500 mb-4">
            Export your records with a status and a plain explanation on every row that needs a fix. When it is ready to file,
            submit it as final — that records the date, the period, and whether errors existed, and puts it in front of a
            compliance reviewer who can accept it or send it back for correction.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={exportErrors} disabled={exporting || !(reconcileCensus || census)}
              className="border border-slate-800 text-slate-800 hover:bg-slate-50 disabled:border-slate-300 disabled:text-slate-400 px-4 py-2 text-sm font-semibold transition-colors">
              {exporting ? 'Preparing…' : 'Export errors to Excel'}
            </button>
            <button onClick={submitAsFinal} disabled={submitting}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold transition-colors">
              {submitting ? 'Recording…' : 'Submit as final'}
            </button>
            {reconcileResult && (
              <span className="text-[12px] text-slate-500">
                {reconcileResult.reconciliation?.match
                  ? 'Reconciled clean — nothing outstanding.'
                  : `${(reconcileResult.reconciliation?.findings || []).length} discrepanc${(reconcileResult.reconciliation?.findings || []).length === 1 ? 'y' : 'ies'} will be recorded with this submission.`}
              </span>
            )}
          </div>
          {!reconcileCensus && (
            <div className="text-[12px] text-slate-500 mt-2">The row-level export needs your case-level records (add them in Step 2).</div>
          )}
          {exportNote && (
            <div className={`mt-3 border text-sm px-3 py-2 ${exportNote.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{exportNote.text}</div>
          )}
          {submitNote && (
            <div className={`mt-3 border text-sm px-3 py-2 ${submitNote.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{submitNote.text}</div>
          )}
        </div>
      )}
    </div>
  );
}

// The formulas behind Section II, verbatim from the CY2026 layout and the
// technical specifications — the exact rules the engine applies. Static and
// certain; expandable so they sit next to the numbers without crowding them.
function OdrFormulas() {
  return (
    <details className="mb-4 border border-slate-300 bg-slate-50/60 px-4 py-2.5">
      <summary className="cursor-pointer text-sm font-semibold text-slate-800">
        The formulas — how every number is calculated (per the CY2026 layout)
      </summary>
      <div className="mt-2 text-[13px] text-slate-700 space-y-2">
        <div>
          <span className="font-semibold">Which period a case counts in:</span> this tool places a case in the period its <b>Decision Date</b> falls in, regardless of when the request was received. CMS counts an organization determination as completed when the MAO has notified the enrollee of its decision (CY2026 Part C Technical Specifications, Section II); when your case list carries notification dates, a case outside the period is shown with both dates.
        </div>
        <div>
          <span className="font-semibold">Totals (Sub1 / Sub3):</span>
          <ul className="list-disc list-inside mt-0.5 space-y-0.5">
            <li><b>A</b> = cases with a substantive decision (Fully favorable, Partially favorable, or Adverse) in the period. Withdrawn and dismissed cases are <b>not</b> in A.</li>
            <li><b>B</b> = withdrawn cases; <b>C</b> = dismissed cases (each its own element, outside A).</li>
            <li><b>D</b> = A-cases that are <b>Services</b>, requested by the enrollee, their representative, or a <b>contract</b> provider on the enrollee's behalf.</li>
            <li><b>E</b> = A-cases that are <b>Claims</b>, submitted by the enrollee or representative.</li>
            <li><b>F</b> = A-cases that are <b>Services</b>, requested by a <b>non-contract</b> provider.</li>
            <li><b>G</b> = A-cases that are <b>Claims</b>, submitted by a <b>non-contract</b> provider.</li>
            <li>So <b>D + E + F + G = A</b>, always — every decided case sits in exactly one of the four.</li>
          </ul>
        </div>
        <div>
          <span className="font-semibold">Dispositions (Sub2 / Sub4):</span> each A-case lands in exactly <b>one</b> of twelve cells — its disposition (Fully favorable → A–D, Partially favorable → E–H, Adverse → I–L) crossed with the same four requester groups. So the <b>twelve cells sum to A</b>.
        </div>
        <div>
          <span className="font-semibold">Sub3/Sub4</span> apply the same formulas to Reconsiderations.
        </div>
        <div>
          <span className="font-semibold">Not counted at all</span> (per the technical specifications): claims from contract providers, IRE decisions, dismissal appeals, duplicate or returned claims, and the other exclusions listed under the verdict's "what this tool cannot see."
        </div>
      </div>
    </details>
  );
}

// Counts-vs-census verdict: green when the records derive the same counts, amber
// with the exact element, the submitted value, and the census value when not.
function ReconcileResult({ data }) {
  const rec = data.reconciliation || {};
  const clean = !!rec.match;
  const cen = rec.census || {};
  const matched = rec.matched != null ? rec.matched : (rec.checked - (rec.findings || []).length);
  return (
    <section className={`mt-4 border-2 ${clean ? 'border-emerald-200' : 'border-amber-300'}`}>
      <div className={`px-4 py-3 ${clean ? 'bg-emerald-50' : 'bg-amber-50'}`}>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counts vs records</div>
        <div className={`text-sm font-bold ${clean ? 'text-emerald-800' : 'text-amber-800'}`}>
          {rec.summary || (clean ? `Reconciled — all ${rec.checked} elements match.` : `${matched} of ${rec.checked} elements reconcile; ${(rec.findings || []).length} do not.`)}
        </div>
        {rec.reviewed?.statement && (
          <div className="text-[13px] font-semibold text-slate-800 mt-1">{rec.reviewed.statement}</div>
        )}
        {(cen.rows_read != null) && (
          <div className="text-[12px] text-slate-600 mt-1">
            Records: read <b>{cen.rows_read}</b> row{cen.rows_read === 1 ? '' : 's'}, counted <b>{cen.rows_counted}</b> for <b>{cen.period}</b>
            {cen.observed_date_range && <> · records dated {cen.observed_date_range.min} to {cen.observed_date_range.max}</>}
          </div>
        )}
      </div>

      {/* The verdict: the one line the user came for. READY only when every
          check the tool can run passed; otherwise each blocker, in order. */}
      {rec.verdict && (
        <div className={`px-4 py-3 border-t-4 ${rec.verdict.ready ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
          <div className={`text-[15px] font-bold ${rec.verdict.ready ? 'text-emerald-900' : 'text-red-900'}`}>
            {rec.verdict.headline}
          </div>
          {rec.cause?.text && (
            <div className="mt-1.5 text-[13.5px] font-semibold text-red-900">{rec.cause.text}</div>
          )}
          {/* Stage ladder: which step passed, which did not — at a glance,
              before any detail. "This passed, this one didn't." */}
          {(rec.verdict.stages || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {rec.verdict.stages.map((s, i) => (
                <div key={i} className={`text-[13px] ${s.ok ? 'text-emerald-800' : 'text-red-900'}`}>
                  <span className="font-bold">{s.ok ? '✓' : '✗'} {s.label}: </span>{s.text}
                </div>
              ))}
            </div>
          )}
          {(rec.verdict.blockers || []).length > 0 && (
            <ol className="mt-2 space-y-1 text-[13px] text-red-900 list-decimal list-inside">
              {rec.verdict.blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ol>
          )}
          {(rec.verdict.manual_checks || []).length > 0 && (
            <details className="mt-2 text-[12.5px] text-slate-700">
              <summary className="cursor-pointer font-semibold">
                Required exclusions — these must NOT be in your submission; confirm your export left them out ({rec.verdict.manual_checks.length})
              </summary>
              {rec.verdict.manual_checks_intro && (
                <div className="mt-1 mb-1 text-slate-600">{rec.verdict.manual_checks_intro}</div>
              )}
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {rec.verdict.manual_checks.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* The formulas, one click away from the numbers they explain. */}
      {data.measure_id === 'odr' && (
        <div className="px-4 pt-3">
          <OdrFormulas />
        </div>
      )}

      {/* Notes: year-in-the-rear re-anchor, or why the records counted zero. */}
      {(rec.notes || []).length > 0 && (
        <div className="px-4 py-2 bg-sky-50 border-t border-sky-200 text-[13px] text-sky-900 space-y-1">
          {rec.notes.map((n, i) => <div key={i}>{n}</div>)}
        </div>
      )}

      {/* Scope contradictions: the file says one thing, the user's answer says
          another. These outrank per-element findings — show them first, loud. */}
      {(rec.scope?.conflicts || []).length > 0 && (
        <div className="px-4 py-3 bg-red-50 border-t-2 border-red-300">
          <div className="text-[13px] font-bold text-red-800 mb-2">
            ⚠ The file does not match your answer about what this submission covers:
          </div>
          <div className="space-y-2">
            {rec.scope.conflicts.map((c, i) => (
              <div key={i} className="border border-red-200 bg-white px-3 py-2 text-sm text-slate-800">{c.message}</div>
            ))}
          </div>
        </div>
      )}

      {/* What the case list could not verify — said once, plainly, instead of
          surfacing as dozens of fake mismatches. */}
      {(rec.scope?.unverifiable || []).length > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-[13px] text-amber-900 space-y-1.5">
          {rec.scope.unverifiable.map((g, i) => (
            <div key={i}>
              <span className="font-semibold">Could not be checked: </span>{g.reason}
              <span className="text-amber-700"> ({g.elements.length} element{g.elements.length === 1 ? '' : 's'} skipped)</span>
            </div>
          ))}
        </div>
      )}

      {/* Out-of-scope elements skipped by design — one quiet line. */}
      {(rec.scope?.skipped_out_of_scope || []).length > 0 && (
        <div className="px-4 py-2 border-t border-slate-200 text-[12px] text-slate-500">
          {rec.scope.skipped_out_of_scope.length} element{rec.scope.skipped_out_of_scope.length === 1 ? '' : 's'} belong to areas you said this submission does not include — they were not compared.
        </div>
      )}

      {/* What passed. */}
      {matched > 0 && (
        <div className="px-4 pt-3 text-[13px] text-emerald-800">
          <span className="font-semibold">✓ {matched} element{matched === 1 ? '' : 's'} reconcile</span> — the records derive the same submitted count.
        </div>
      )}

      {/* What failed, where exactly, and what to do. Every line is computed
          from the two files — rows, columns, and both remediation options are
          deterministic; the app never guesses which side is wrong. */}
      {(rec.findings || []).length > 0 && (
        <div className="px-4 py-3">
          <div className="text-[13px] font-semibold text-amber-800 mb-2">✗ {rec.findings.length} element{rec.findings.length === 1 ? ' does' : 's do'} not reconcile:</div>
          <div className="space-y-3">
            {rec.findings.map((f, i) => (
              <div key={i} className="border border-amber-200 bg-amber-50/50 px-3.5 py-3 text-sm text-slate-800">
                <div className="font-semibold text-slate-900">
                  {f.label || f.element}
                </div>
                {/* Plain headline first — user language, computed only from
                    the two files; the technical statement stays underneath. */}
                {f.plain && (
                  <div className="mt-1 text-[13.5px] font-semibold text-amber-900">
                    {f.plain}
                  </div>
                )}
                <div className={`mt-0.5 ${f.plain ? 'text-[12.5px] text-slate-600' : ''}`}>
                  {f.message || `${f.element}: counts ${f.submitted}, records ${f.census} (diff ${f.diff})`}
                </div>
                {f.where && (
                  <div className="mt-2 text-[13px]">
                    <span className="font-semibold text-slate-700">Where: </span>{f.where}
                  </div>
                )}
                {f.remedy && (
                  <div className="mt-1.5 text-[13px] text-slate-700">
                    <span className="font-semibold">What to do: </span>{f.remedy}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// Plain-language "why it failed" panel. Renders the engine diagnostics so the
// user sees exactly which column or value the file was missing — not a bare
// error. Shows only what is present in the diagnostics object.
function FailureDiagnostics({ diag }) {
  if (!diag) return null;
  const headers = diag.headers || (diag.columns_resolved ? null : null);
  const resolved = diag.columns_resolved || {};
  const missing = diag.missing || [];
  const unrec = diag.unrecognized_values || {};
  const excluded = diag.excluded_by_reason || {};
  const hasResolved = Object.keys(resolved).length > 0;
  const hasUnrec = Object.keys(unrec).length > 0;
  const hasExcluded = Object.keys(excluded).length > 0;

  return (
    <div className="mt-3 border border-amber-300 bg-amber-50 text-slate-800 text-sm px-4 py-3 space-y-3">
      <div className="text-xs font-mono uppercase tracking-widest text-amber-800">What the check found</div>

      {(diag.rows_read != null || diag.rows_counted != null) && (
        <div className="text-sm">
          Read <b>{diag.rows_read ?? '—'}</b> row{diag.rows_read === 1 ? '' : 's'}
          {diag.rows_counted != null && <> · counted <b>{diag.rows_counted}</b></>}
          {diag.format && <> · read as <span className="font-mono">{diag.format}</span>{diag.sheet ? ` (sheet "${diag.sheet}")` : ''}</>}
        </div>
      )}

      {missing.length > 0 && (
        <div>
          <div className="font-semibold text-red-700">Missing required column{missing.length === 1 ? '' : 's'}:</div>
          <div className="font-mono text-[13px]">{missing.join(', ')}</div>
          {Array.isArray(diag.headers) && (
            <div className="mt-1 text-[12px] text-slate-600">Your file's columns: <span className="font-mono">{diag.headers.join(', ') || '(none)'}</span></div>
          )}
        </div>
      )}

      {hasUnrec && (
        <div>
          <div className="font-semibold text-red-700">Values not recognized (those rows were not counted):</div>
          <ul className="list-disc list-inside text-[13px]">
            {Object.entries(unrec).map(([field, vals]) => (
              <li key={field}><span className="font-mono">{field}</span>: {vals.map(v => `"${v.value}" ×${v.count}`).join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      {hasResolved && (
        <details className="text-[12px] text-slate-600">
          <summary className="cursor-pointer font-semibold">Columns matched ({Object.keys(resolved).length})</summary>
          <ul className="list-disc list-inside mt-1">
            {Object.entries(resolved).map(([field, col]) => (
              <li key={field}><span className="font-mono">{field}</span> ← <span className="font-mono">{String(col)}</span></li>
            ))}
          </ul>
        </details>
      )}

      {hasExcluded && (
        <details className="text-[12px] text-slate-600">
          <summary className="cursor-pointer font-semibold">Why rows were excluded</summary>
          <ul className="list-disc list-inside mt-1">
            {Object.entries(excluded).sort((a, b) => b[1] - a[1]).map(([reason, n]) => (
              <li key={reason}><span className="font-mono">{reason}</span>: {n}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CanonicalReportPanel({ report }) {
  const { mode, data } = report;
  // preflight returns the file-shape result directly; full nests it under file_shape.
  const fileShape = mode === 'preflight' ? data : data.file_shape;
  const counts = mode === 'full' ? data.derived : (mode === 'recount' ? data.counts : null);
  const engineWarnings = mode === 'full' ? (data.engine_warnings || []) : (mode === 'recount' ? (data.warnings || []) : []);
  // Section II Counts + Records: the full reconciliation assembly (named
  // cases with both dates, the level reviewed, READY / NOT READY). When it is
  // present it replaces the bare element list and the derived-figures check.
  const detail = mode === 'full' && data.reconciliation_detail ? data.reconciliation_detail : null;

  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900">
          {mode === 'preflight' ? 'File check result' : mode === 'recount' ? 'Records recount result' : 'File check + Records recount result'}
        </h3>
        <div className="text-xs font-mono text-slate-500 mt-1">
          Measure {data.measure_id || '—'} · CY {data.cy || '—'}
          {fileShape?.schema_checksum ? ` · schema ${String(fileShape.schema_checksum).slice(0, 12)}` : ''}
        </div>
      </div>

      {/* The headline answer: do the counts match the records? Stated first,
          in words, before any table. */}
      {detail && (
        <div className="mb-4">
          <ReconcileResult data={{ reconciliation: detail, measure_id: data.measure_id }} />
        </div>
      )}

      {!detail && data.counts_vs_census && (
        <section className={`mb-4 border-2 ${data.counts_vs_census.match ? 'border-emerald-400' : 'border-red-400'}`}>
          <div className={`px-4 py-3 ${data.counts_vs_census.match ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counts vs records (census)</div>
            <div className={`text-sm font-bold ${data.counts_vs_census.match ? 'text-emerald-800' : 'text-red-800'}`}>
              {data.counts_vs_census.statement}
            </div>
          </div>
        </section>
      )}

      {/* Cases the count left OUT — one line per case, reason in plain words.
          A person reviewing a run must SEE a stray case, not find it in a
          diagnostics blob, even when the remaining arithmetic still balances. */}
      {data.diagnostics?.excluded_cases?.length > 0 && (
        <section className="mb-4 border-2 border-amber-400">
          <div className="px-4 py-2 bg-amber-50">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Excluded cases</div>
            <div className="text-sm font-bold text-amber-900">
              {data.diagnostics.excluded_cases.length} case(s) in your records were NOT counted — each one is listed with the reason.
            </div>
          </div>
          <div className="px-4 py-2 space-y-1 max-h-64 overflow-y-auto">
            {data.diagnostics.excluded_cases.map((c, i) => (
              <div key={i} className="text-[13px] text-amber-950">
                <span className="font-semibold">{c.case_id ? `Case ${c.case_id}` : 'Case (no id)'} · row {c.excel_row || c.row}:</span> {c.reason}
                {c.case_date ? ` — Decision Date ${c.case_date}` : ''}
                {c.belongs_in ? `, which belongs in ${c.belongs_in}` : ''}
                {c.notified_date ? `; enrollee notified ${c.notified_date}` : ''}.
              </div>
            ))}
            <div className="text-[11px] text-slate-500 pt-1">
              If a case listed here belongs in this reporting period, fix it in the source system and re-run. Excluded cases are never counted and never altered.
            </div>
          </div>
        </section>
      )}

      {/* Cases that COUNT but cannot be fully placed: an unreadable requester
          code keeps the case in the totals while the who-requested breakdown
          and the disposition table stay unverifiable. Distinct from excluded
          cases (those never count) — these are the rows behind an off-by-N
          arithmetic failure, named so the fix is a lookup, not a hunt. */}
      {data.diagnostics?.attention_cases?.length > 0 && (
        <section className="mb-4 border-2 border-orange-400">
          <div className="px-4 py-2 bg-orange-50">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counted, but needs a fix</div>
            <div className="text-sm font-bold text-orange-900">
              {data.diagnostics.attention_cases.length} case(s) ARE in the totals but could not be placed in the who-requested breakdown — each one is listed with what to fix.
            </div>
          </div>
          <div className="px-4 py-2 space-y-1 max-h-64 overflow-y-auto">
            {data.diagnostics.attention_cases.map((c, i) => (
              <div key={i} className="text-[13px] text-orange-950">
                <span className="font-semibold">{c.case_id ? `Case ${c.case_id}` : 'Case (no id)'} · row {c.row}:</span> {c.problem}. To fix: {c.fix}.
              </div>
            ))}
            <div className="text-[11px] text-slate-500 pt-1">
              These cases stay in the totals either way. Until the requester cells are fixed, the D–G split and the disposition table cannot be fully checked.
            </div>
          </div>
        </section>
      )}

      {data.scope?.conflicts?.length > 0 && (
        <section className="mb-4 border-2 border-red-300">
          <div className="px-4 py-2 bg-red-50 text-sm font-semibold text-red-800">
            The figures contradict your answer about what this upload covers ({data.scope.conflicts.length})
          </div>
          <div className="px-4 py-2 space-y-1">
            {data.scope.conflicts.map((c, i) => (
              <div key={i} className="text-[13px] text-red-900">{c.message}</div>
            ))}
          </div>
        </section>
      )}

      {fileShape && !fileShape.skipped && <FileShapeVerdict fileShape={fileShape} />}
      {fileShape && !fileShape.skipped && <FileShapeFindings fileShape={fileShape} />}
      {fileShape?.skipped && (
        <div className="mb-4 text-[12px] text-slate-600 border border-slate-200 bg-slate-50 px-3 py-2">{fileShape.reason}</div>
      )}

      {/* Section II: the formulas behind every number — the exact rules the
          engine applies, from the CY2026 layout, expandable where the numbers
          appear so "how was this calculated?" never needs a support ticket. */}
      {data.measure_id === 'odr' && <OdrFormulas />}

      {/* Section II: the explicit answer to "are the counts correct?" — the
          layout's own arithmetic, run on what was read, pass or fail said
          out loud instead of silence. */}
      {!detail && data.counts_check && (
        <section className={`mb-4 border-2 ${data.counts_check.passed ? 'border-emerald-300' : 'border-red-300'}`}>
          <div className={`px-4 py-3 ${data.counts_check.passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counts check</div>
            <div className={`text-sm font-bold ${data.counts_check.passed ? 'text-emerald-800' : 'text-red-800'}`}>
              {data.counts_check.passed
                ? `COUNTS CHECK PASSED — the figures add up (${data.counts_check.checks.length} arithmetic check${data.counts_check.checks.length === 1 ? '' : 's'}).`
                : `COUNTS CHECK FAILED — the counts file's own arithmetic does not add up.`}
            </div>
          </div>
          <div className="px-4 py-2 space-y-1">
            {data.counts_check.checks.map((c, i) => (
              <div key={i} className={`text-[13px] ${c.ok ? 'text-emerald-800' : 'text-red-900'}`}>
                <span className="font-semibold">{c.ok ? '✓' : '✗'} {c.label}: </span>{c.detail}
              </div>
            ))}
          </div>
        </section>
      )}

      {mode === 'full' && !detail && <CanonicalReconciliation findings={data.reconciliation || []} />}

      {data.diagnostics?.mode === 'pre_aggregated' && (
        <div className="mb-4">
          <div className="text-xs font-mono text-slate-500">
            Read pre-computed counts from the summary sheet · contract {data.diagnostics.contract || '—'} · period {data.diagnostics.period || '—'}
          </div>
          {Array.isArray(data.diagnostics.blocks) && data.diagnostics.blocks.length > 0 && (
            <div className="mt-1 text-[12px] text-slate-600">
              <span className="font-semibold">Sections the vendor provided:</span>
              <ul className="list-disc list-inside">
                {data.diagnostics.blocks.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
              <div className="text-[11px] text-slate-400 mt-0.5">Sections not in the file read as zero — that is treated as "not provided," not an error.</div>
            </div>
          )}
        </div>
      )}
      {data.diagnostics?.validation && <CountsSelfCheck v={data.diagnostics.validation} />}

      {counts && <DerivedCountsSection counts={counts} />}

      {data.diagnostics?.monthly && <MonthlyBreakdown monthly={data.diagnostics.monthly} />}

      {engineWarnings.length > 0 && <EngineWarnings warnings={engineWarnings} />}
    </div>
  );
}

// Month-by-month table, read straight from the spreadsheet's month columns, so a
// whole year can be reviewed one month at a time. One table per subsection; only
// elements with a non-zero annual total are shown to keep it readable.
function MonthlyBreakdown({ monthly }) {
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const subs = Object.keys(monthly || {}).filter(k => Object.keys(monthly[k] || {}).length);
  if (!subs.length) return null;
  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Month by month</h4>
      {subs.map(sub => {
        const nonZero = Object.entries(monthly[sub]).filter(([, v]) => (v.total || 0) !== 0);
        const rows = nonZero.length ? nonZero : Object.entries(monthly[sub]).slice(0, 1);
        return (
          <div key={sub} className="mb-4 overflow-x-auto">
            <div className="text-xs font-mono text-slate-600 mb-1">{sub}</div>
            <table className="text-xs border-collapse">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left pr-2 py-1">Elem</th>
                  {M.map(m => <th key={m} className="px-2 py-1 text-right font-normal">{m}</th>)}
                  <th className="px-2 py-1 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([L, v]) => (
                  <tr key={L} className="border-t border-slate-100">
                    <td className="pr-2 py-1 font-mono">{L}</td>
                    {(v.months || []).map((n, i) => <td key={i} className="px-2 py-1 text-right font-mono">{n == null ? '—' : n}</td>)}
                    <td className="px-2 py-1 text-right font-mono font-bold">{v.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      <div className="text-[11px] text-slate-400">Read straight from your spreadsheet's month columns. Only elements with a non-zero total are shown.</div>
    </section>
  );
}

// The gentle counts validation: did the sheet's months add up to its quarters
// and its quarters to its annual totals? Green when clean, amber with the exact
// element and amount when not.
function CountsSelfCheck({ v }) {
  if (!v || !v.checks) return null;
  const clean = v.mismatches === 0;
  return (
    <section className={`mb-6 border-2 ${clean ? 'border-emerald-200' : 'border-amber-300'}`}>
      <div className={`px-4 py-3 ${clean ? 'bg-emerald-50' : 'bg-amber-50'}`}>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Counts self-check</div>
        <div className={`text-sm font-bold ${clean ? 'text-emerald-800' : 'text-amber-800'}`}>
          {clean
            ? `All ${v.checks} totals add up — months match quarters, quarters match the annual total.`
            : `${v.mismatches} of ${v.checks} totals do not add up.`}
        </div>
      </div>
      {!clean && (v.findings || []).length > 0 && (
        <ul className="px-4 py-2 text-sm text-slate-800 list-disc list-inside space-y-0.5">
          {v.findings.slice(0, 12).map((f, i) => <li key={i}>{f.message || String(f)}</li>)}
        </ul>
      )}
    </section>
  );
}

function FileShapeVerdict({ fileShape }) {
  const ok = !!fileShape.ok;
  const s = fileShape.summary || {};
  return (
    <section className="mb-6 border-2 border-slate-200">
      <div className={`px-4 py-3 flex items-center justify-between ${ok ? 'bg-emerald-50' : 'bg-red-50'}`}>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">File shape</div>
          <div className={`text-base font-bold ${ok ? 'text-emerald-800' : 'text-red-800'}`}>
            {ok ? 'HPMS WILL ACCEPT THIS FILE SHAPE' : 'HPMS WILL REJECT THIS FILE'}
          </div>
        </div>
        <div className={`px-3 py-1 text-xs font-mono border ${ok ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-red-50 text-red-800 border-red-300'}`}>
          {s.errors || 0} error{s.errors === 1 ? '' : 's'} · {s.warnings || 0} warning{s.warnings === 1 ? '' : 's'} · {fileShape.row_count ?? 0} row{fileShape.row_count === 1 ? '' : 's'}
        </div>
      </div>
    </section>
  );
}

// Defect 4 fix: render the per-field file-shape findings (row_index, position,
// element, code, message) that the backend has always returned but the UI
// never displayed.
function FileShapeFindings({ fileShape }) {
  const errors = fileShape.errors || [];
  const warnings = fileShape.warnings || [];
  const infos = fileShape.infos || [];
  if (errors.length === 0 && warnings.length === 0 && infos.length === 0) {
    return (
      <section className="mb-6">
        <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">File shape findings</h4>
        <div className="text-sm text-emerald-800">
          Every position check passed. Rows, delimiters, and element values match the CMS file record layout.
        </div>
      </section>
    );
  }
  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">File shape findings</h4>
      <div className="space-y-2">
        {errors.map((f, i) => <FileShapeFindingRow key={`e-${i}`} f={f} cls="border-red-700 bg-red-50" badge="ERROR"/>)}
        {warnings.map((f, i) => <FileShapeFindingRow key={`w-${i}`} f={f} cls="border-amber-600 bg-amber-50" badge="WARNING"/>)}
        {infos.map((f, i) => <FileShapeFindingRow key={`i-${i}`} f={f} cls="border-slate-300 bg-slate-50" badge="INFO"/>)}
      </div>
    </section>
  );
}

function FileShapeFindingRow({ f, cls, badge }) {
  const bits = [badge];
  if (f.row_index != null) bits.push(`row ${f.row_index}`);
  if (f.position != null) bits.push(`pos ${f.position}`);
  if (f.element && f.element !== '-') bits.push(`elt ${f.element}`);
  if (f.subsection_id) bits.push(`sub ${f.subsection_id}`);
  if (f.code) bits.push(f.code);
  return (
    <div className={`border-l-4 px-3 py-2 ${cls}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-600 mb-1">{bits.join(' · ')}</div>
      <div className="text-sm text-slate-900">{f.message}</div>
    </div>
  );
}

function CanonicalReconciliation({ findings }) {
  if (findings.length === 0) {
    return (
      <section className="mb-6">
        <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Reconciliation</h4>
        <div className="text-sm text-emerald-800">
          Your file matches your records. Every submitted count equals the count we derived from the census.
        </div>
      </section>
    );
  }
  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Reconciliation discrepancies</h4>
      <div className="space-y-2">
        {findings.map((f, i) => (
          <div key={i} className={`border-l-4 px-3 py-2 ${f.severity === 'warning' ? 'border-amber-600 bg-amber-50' : 'border-red-700 bg-red-50'}`}>
            <div className="text-sm font-semibold text-slate-900">
              {f.element ? `Element ${f.element}` : 'Row mismatch'}
              {f.subsection_id ? ` · Subsection ${f.subsection_id}` : ''}
              {f.plan_key ? ` · Plan ${f.plan_key}` : ''}
              {f.submitted !== undefined && f.derived !== undefined
                ? ` — submitted ${f.submitted}, derived ${f.derived} (Δ ${f.delta > 0 ? `+${f.delta}` : f.delta})`
                : ''}
            </div>
            {f.message && <div className="text-xs text-slate-700 mt-1">{f.message}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function DerivedCountsSection({ counts }) {
  const total = counts?.total || {};
  const entries = Object.entries(total);
  const flat = entries.filter(([, v]) => !(v && typeof v === 'object'));
  const nested = entries.filter(([, v]) => v && typeof v === 'object');
  const planCount = counts?.byPlan ? Object.keys(counts.byPlan).length : 0;
  if (flat.length === 0 && nested.length === 0 && planCount === 0) return null;

  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Derived counts from your records</h4>
      {flat.length > 0 && <CountRows rows={flat} />}
      {nested.map(([groupKey, group]) => (
        <div key={groupKey} className="mt-3">
          <div className="text-xs font-mono text-slate-600 mb-1">{groupKey}</div>
          <CountRows rows={Object.entries(group)} />
        </div>
      ))}
      {planCount > 0 && (
        <div className="text-xs font-mono text-slate-500 mt-2">{planCount} plan{planCount === 1 ? '' : 's'} counted.</div>
      )}
    </section>
  );
}

function CountRows({ rows }) {
  return (
    <table className="w-full text-sm max-w-md">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-slate-100">
            <td className="py-1 font-mono">{k}</td>
            <td className="py-1 text-right font-mono">{String(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EngineWarnings({ warnings }) {
  return (
    <section>
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Engine warnings</h4>
      <div className="space-y-1">
        {warnings.map((w, i) => (
          <div key={i} className="border-l-4 border-amber-600 bg-amber-50 px-3 py-2 text-sm text-slate-900">
            {typeof w === 'string' ? w : (w?.message || JSON.stringify(w))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ReporterRunAgentActivity — polls /api/agent-progress for the in-flight
// reporter validation run. Mirrors the OracleHome DocumentAgentActivity
// pattern.
// ---------------------------------------------------------------------------
function ReporterRunAgentActivity({ runId }) {
  const { events, status } = useAgentProgress('reporter_run', runId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}

function FileDrop({ label, description, file, onChange, compact }) {
  const inputRef = useRef(null);
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-slate-300 hover:border-red-700 bg-slate-50 px-3 py-4 cursor-pointer transition-colors"
      >
        <input ref={inputRef} type="file" className="hidden" onChange={e => onChange(e.target.files?.[0] || null)}/>
        {file ? (
          <div className="font-mono text-sm text-slate-900">
            ✓ {file.name} <span className="text-slate-500 text-xs">({Math.round(file.size / 1024)} KB)</span>
          </div>
        ) : (
          <div className="text-sm text-slate-500">Click to upload</div>
        )}
        {!compact && <div className="text-xs text-slate-500 mt-1">{description}</div>}
      </div>
    </div>
  );
}

function ReportPanel({ report, mode, sectionId }) {
  // mode='preflight' → only the HPMS panel + the parsed submission counts.
  // mode='full' → preflight + reconciliation + classification flags.
  const isPreflightOnly = mode === 'preflight';
  const { hpmsPreflight, reconciliation, classificationFlags, runId, elapsedMs, submission, derived } = report;
  const preflight = hpmsPreflight || (report.willPass !== undefined ? report : null);

  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{isPreflightOnly ? 'File check result' : 'File check + Records recount result'}</h3>
          <div className="text-xs font-mono text-slate-500 mt-1">
            Run {runId || '—'}{elapsedMs ? ` · ${elapsedMs} ms` : ''}{!isPreflightOnly && derived ? ' · Records parsed' : ''}
          </div>
        </div>
      </div>

      {preflight && <HpmsPreflightPanel preflight={preflight}/>}

      {!isPreflightOnly && submission?.counts && Object.keys(submission.counts).length > 0 && (
        <CountsTable submission={submission} derived={derived} sectionId={sectionId}/>
      )}

      {!isPreflightOnly && reconciliation?.findings?.length > 0 && (
        <FindingsList title="Reconciliation Discrepancies" items={reconciliation.findings}/>
      )}

      {!isPreflightOnly && classificationFlags?.length > 0 && (
        <FlagsList title="Classification Flags" items={classificationFlags}/>
      )}

      {isPreflightOnly && submission?.counts && Object.keys(submission.counts).length > 0 && (
        <section className="mt-4">
          <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Parsed counts from the .txt</h4>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr><th className="text-left py-2 w-16">Element</th><th className="text-right py-2">Value</th></tr>
            </thead>
            <tbody>
              {Object.entries(submission.counts).map(([eid, v]) => (
                <tr key={eid} className="border-b border-slate-100">
                  <td className="py-1 font-mono">{eid}</td>
                  <td className="py-1 text-right font-mono">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function HpmsPreflightPanel({ preflight }) {
  const { willPass, errors = [], warnings = [], info = [], summary = {}, sectionMeta } = preflight;
  const verdict = willPass ? 'HPMS WILL ACCEPT THIS FILE' : 'HPMS WILL REJECT THIS FILE';

  return (
    <section className="mb-6 border-2 border-slate-200">
      <div className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between ${willPass ? 'bg-emerald-50' : 'bg-red-50'}`}>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">File check</div>
          <div className={`text-base font-bold ${willPass ? 'text-emerald-800' : 'text-red-800'}`}>{verdict}</div>
        </div>
        <div className={`px-3 py-1 text-xs font-mono border ${willPass ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-red-50 text-red-800 border-red-300'}`}>
          {summary.critical || 0} critical · {summary.high || 0} warning · {summary.totalChecksRun || 0} checks
        </div>
      </div>
      <div className="p-4 space-y-3">
        {sectionMeta?.due && (
          <div className="text-xs text-slate-600 font-mono">
            Submission window: {sectionMeta.due} · Level: {sectionMeta.level} · Frequency: {sectionMeta.frequency}{sectionMeta.dv ? ' · Data Validation required' : ''}
          </div>
        )}
        {!willPass && (
          <div className="text-xs text-slate-700 italic">
            Fix every critical below before uploading. HPMS rejects the entire file on any one of these.
          </div>
        )}
        {errors.map((e, i) => <Finding key={`e-${i}`} f={e} cls="border-red-700 bg-red-50" badge="CRITICAL"/>)}
        {warnings.map((w, i) => <Finding key={`w-${i}`} f={w} cls="border-amber-600 bg-amber-50" badge="WARNING"/>)}
        {info.map((n, i) => <Finding key={`i-${i}`} f={n} cls="border-slate-300 bg-slate-50" badge="INFO"/>)}
        {willPass && errors.length === 0 && warnings.length === 0 && (
          <div className="text-sm text-emerald-800">
            Every check passed. The file's shape, contents, and internal math match what HPMS expects.
            HPMS may still run extra edits at upload time; this check covers everything inferable from the public CMS rules.
          </div>
        )}
      </div>
    </section>
  );
}

function Finding({ f, cls, badge }) {
  return (
    <div className={`border-l-4 px-3 py-2 ${cls}`}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-600 mb-1">
        {badge} · {f.rule}{f.field ? ` · ${f.field}` : ''}{f.value !== undefined ? ` = "${f.value}"` : ''}
      </div>
      <div className="text-sm text-slate-900">{f.message}</div>
      {f.citation && <div className="text-xs font-mono text-slate-500 mt-1">{f.citation}</div>}
    </div>
  );
}

function CountsTable({ submission, derived, sectionId }) {
  const derivedCounts = derived?.counts || derived?.subsection_1 || derived?.subsection_3 || {};
  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Submitted vs derived</h4>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
          <tr>
            <th className="text-left py-2 w-12">Elt</th>
            <th className="text-right py-2 w-24">Submitted</th>
            <th className="text-right py-2 w-24">Derived</th>
            <th className="text-right py-2 w-20">Δ</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(submission.counts).map(([eid, sval]) => {
            const dval = derivedCounts[eid] ?? 0;
            const delta = sval - dval;
            return (
              <tr key={eid} className="border-b border-slate-100">
                <td className="py-2 font-mono">{eid}</td>
                <td className="py-2 text-right font-mono">{sval}</td>
                <td className="py-2 text-right font-mono">{dval}</td>
                <td className={`py-2 text-right font-mono ${delta === 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {delta > 0 ? `+${delta}` : delta}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function FindingsList({ title, items }) {
  return (
    <section className="mb-6">
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</h4>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={i} className="border-l-4 border-red-700 bg-red-50 px-3 py-2">
            <div className="text-sm font-semibold text-slate-900">
              Element {f.elementId} — submitted {f.submission_value}, derived {f.derived_value} (Δ {f.delta > 0 ? `+${f.delta}` : f.delta})
            </div>
            {f.probable_cause && <div className="text-xs text-slate-700 mt-1">{f.probable_cause}</div>}
            {f.citation && <div className="text-xs font-mono text-slate-500 mt-1">{f.citation}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function FlagsList({ title, items }) {
  return (
    <section>
      <h4 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</h4>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={i} className={`border-l-4 px-3 py-2 ${
            f.severity === 'critical' ? 'border-red-700 bg-red-50' :
            f.severity === 'high' ? 'border-amber-600 bg-amber-50' : 'border-slate-300 bg-slate-50'
          }`}>
            <div className="text-sm">
              <span className="font-mono text-xs uppercase tracking-widest mr-2">{f.severity}</span>
              <span className="font-semibold">{f.rule}</span> · case {f.case_id}
            </div>
            <div className="text-xs font-mono text-slate-500 mt-1">{f.citation}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
